import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  schema?: object;
}

export function SEO({
  title,
  description,
  keywords,
  canonical,
  ogTitle,
  ogDescription,
  ogType = 'website',
  ogUrl,
  twitterTitle,
  twitterDescription,
  schema,
}: SEOProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Update link tags
    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      
      element.href = href;
    };

    // Basic meta tags
    if (description) {
      updateMetaTag('description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Canonical URL
    const canonicalUrl = canonical || `${siteUrl}${location.pathname}`;
    updateLinkTag('canonical', canonicalUrl);

    // Last modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Open Graph tags
    if (ogTitle || title) {
      updateMetaTag('og:title', ogTitle || title || '', true);
    }

    if (ogDescription || description) {
      updateMetaTag('og:description', ogDescription || description || '', true);
    }

    updateMetaTag('og:type', ogType, true);

    if (ogUrl || canonicalUrl) {
      updateMetaTag('og:url', ogUrl || canonicalUrl, true);
    }

    // Twitter Card tags
    if (twitterTitle || title) {
      updateMetaTag('twitter:title', twitterTitle || title || '');
    }

    if (twitterDescription || description) {
      updateMetaTag('twitter:description', twitterDescription || description || '');
    }

    // Schema.org JSON-LD
    if (schema) {
      let schemaScript = document.querySelector('script[type="application/ld+json"]');
      
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(schemaScript);
      }

      const schemaWithContext = {
        '@context': 'https://schema.org',
        ...schema,
      };

      schemaScript.textContent = JSON.stringify(schemaWithContext);
    }

    // Cleanup function
    return () => {
      // Remove schema script on unmount
      const schemaScript = document.querySelector('script[type="application/ld+json"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, keywords, canonical, ogTitle, ogDescription, ogType, ogUrl, twitterTitle, twitterDescription, schema, location.pathname, siteUrl]);

  return null;
}
